import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ErrorRetry from "@/components/ErrorRetry";
import TeamMessages from "@/components/TeamMessages";
import { useNotifications } from "@/context/NotificationContext";
import { usePolling } from "@/lib/usePolling";
import { THREAD_POLL_MS, THREAD_LIST_POLL_MS } from "@/lib/polling";
import { MessageSquare, Send, Loader2, Inbox } from "lucide-react";

export default function Messages() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  // Error OBJECTS, not strings: ErrorRetry owns the wording so a server response can
  // never reach the screen. See lib/errors.js.
  const [listErr, setListErr] = useState(null);
  const [threadErr, setThreadErr] = useState(null);
  const [sendErr, setSendErr] = useState(null);
  const endRef = useRef(null);
  // Clicking a "new message from …" toast lands here with the thread named, which is
  // what makes the alert worth raising: being told somebody wrote to you and then having
  // to find them in a list is most of the work the notification was meant to save.
  const [params, setParams] = useSearchParams();
  const requested = params.get("patient");
  // Patients and team on ONE page, so there is one Messages nav item and one badge.
  // Two sidebar entries competing for the same word is how people stop trusting both.
  const view = params.get("view") === "team" ? "team" : "patients";
  const { feed } = useNotifications();
  const teamUnread = Number(feed?.staff_messages?.unread) || 0;
  const patientsWaiting = Number(feed?.patient_messages?.awaiting_reply_threads) || 0;
  const setView = (next) => {
    const p = new URLSearchParams(params);
    if (next === "team") p.set("view", "team"); else p.delete("view");
    p.delete("conversation");
    setParams(p, { replace: true });
  };

  const loadThreads = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data);
      setListErr(null);
      if (!active && data.length) setActive(data[0].patient_id);
    } catch (e) {
      setListErr(e);
    }
  }, [active]);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  // A deliberate act: following a link to a named thread opens it, and opening it is one
  // of the two things allowed to mark messages read (the other is replying).
  useEffect(() => { if (requested) setActive(requested); }, [requested]);

  /** `scroll` and `markRead` are both false when a POLL refreshed the thread.
   *
   *  scroll: jumping to the bottom every ten seconds would drag the view away from
   *  whatever the reader had scrolled back to look at — most likely the history they
   *  opened the thread to read.
   *
   *  markRead: a poll must never produce a "seen" tick. read_by_staff tells the patient
   *  a human at the clinic looked at their message, and a tab left open at a nurses'
   *  station would otherwise mark everything that arrived overnight as seen by morning.
   *  Only a deliberate act marks read — opening the thread, or replying. Tab visibility
   *  is not evidence anybody read anything. */
  const loadThread = useCallback(async (pid, { scroll = true, markRead = true } = {}) => {
    try {
      const { data } = await api.get(`/messages/${pid}`, { params: { mark_read: markRead } });
      setThread(data);
      setThreadErr(null);
      if (scroll) {
        setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
      }
    } catch (e) {
      setThreadErr(e);
    }
  }, []);

  useEffect(() => { if (active) loadThread(active); }, [active, loadThread]);

  // Live-ish without websockets: the open conversation on a short cycle, the inbox list
  // on a longer one. Both pause when the tab is hidden and refetch on return, and
  // neither resets the idle-logoff timer — see the note at the top of lib/polling.js.
  const pollThread = useCallback(
    () => (active ? loadThread(active, { scroll: false, markRead: false }) : undefined),
    [active, loadThread]);
  usePolling(pollThread, THREAD_POLL_MS, { enabled: Boolean(active) });
  usePolling(loadThreads, THREAD_LIST_POLL_MS);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    setSendErr(null);
    try {
      await api.post(`/messages/${active}`, { body });
      setBody("");   // only after the send actually succeeded
      await loadThread(active);
      loadThreads();
    } catch (e) {
      // Was `catch (err) { /* ignore */ }`. A reply that silently failed looked sent:
      // the draft was cleared and the thread redrew without it. Keep the draft and say
      // so — losing what somebody typed to a patient is the worst of the options.
      setSendErr(e);
    }
    setSending(false);
  };

  return (
    <div className="space-y-6" data-testid="messages-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Messages</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {view === "team"
            ? "Your clinic team. Patients never see these conversations."
            : "One place for patient communication — no more phone tag."}
        </p>
      </div>

      <div className="flex gap-1 border-b border-border">
        {[["patients", "Patients", patientsWaiting], ["team", "Team", teamUnread]].map(
          ([k, label, count]) => (
            <button key={k} data-testid={`messages-view-${k}`} onClick={() => setView(k)}
                    aria-current={view === k ? "page" : undefined}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${view === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {label}
              {count > 0 && (
                <span className="text-[10px] font-semibold tabular-nums bg-secondary text-secondary-foreground rounded-full px-1.5 py-0.5"
                      data-testid={`messages-view-${k}-count`}>{count}</span>
              )}
            </button>
          ))}
      </div>

      {view === "team" ? <TeamMessages /> : (
      <>
      <ErrorRetry
        error={listErr}
        variant="inline"
        onRetry={() => { setListErr(null); loadThreads(); }}
        note="The conversation list may be out of date."
        testid="messages-list-error"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 h-[600px]">
        {/* Thread list */}
        <div className="bg-card border border-border rounded-lg overflow-y-auto" data-testid="thread-list">
          {listErr && threads.length === 0 ? (
            /* "No conversations yet" would be a false statement when the request failed —
               the same empty-means-fine bug as the dashboard's booking requests. */
            <div className="p-6 text-center text-sm text-muted-foreground">
              Couldn't load conversations.
            </div>
          ) : threads.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground"><Inbox className="w-6 h-6 mx-auto mb-2 opacity-50" />No conversations yet.</div>
          ) : threads.map((t) => (
            <button
              key={t.patient_id}
              data-testid={`thread-${t.patient_id}`}
              onClick={() => setActive(t.patient_id)}
              className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-secondary/40 transition-colors ${active === t.patient_id ? "bg-secondary/60" : ""}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm truncate">{t.patient_name}</span>
                {t.awaiting_reply && <span className="shrink-0 text-[10px] font-semibold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">Awaiting reply</span>}
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5">{t.last_body}</p>
            </button>
          ))}
        </div>

        {/* Conversation */}
        <div className="lg:col-span-2 bg-card border border-border rounded-lg flex flex-col">
          {thread ? (
            <>
              <div className="px-5 py-3 border-b border-border">
                <h3 className="font-heading font-semibold" data-testid="thread-patient-name">{thread.patient_name}</h3>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="thread-messages">
                {thread.messages.map((m) => {
                  const mine = m.sender_role === "staff";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
                        <p className="text-sm">{m.body}</p>
                        <p className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-muted-foreground"}`}>{m.sender_name} · {new Date(m.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="px-3 pt-3 space-y-2">
                <ErrorRetry
                  error={threadErr}
                  variant="inline"
                  onRetry={() => { setThreadErr(null); loadThread(active); }}
                  note="This conversation may be out of date."
                  testid="messages-thread-error"
                />
                <ErrorRetry
                  error={sendErr}
                  variant="inline"
                  onRetry={() => setSendErr(null)}
                  note="Your reply was not sent. The text is still in the box."
                  testid="messages-send-error"
                />
              </div>
              <form onSubmit={send} className="p-3 border-t border-border flex gap-2">
                <input
                  data-testid="message-input"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Type a message…"
                  className="flex-1 rounded-md border border-input bg-card px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                />
                <button data-testid="send-message" disabled={sending || !body.trim()} className="bg-primary text-primary-foreground rounded-md px-4 text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 flex items-center gap-1.5">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              <div className="text-center"><MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />Select a conversation</div>
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
}
